/**
 * Join step five: the two constraints the engine scores against.
 *
 * The commute ceiling is the value that removes districts from the candidate
 * set, so it gets a slider bounded by the shared limits rather than free text:
 * the field can then never submit a value the Worker would reject. The budget is
 * a coarse preference and the note is free text for anything the numbers miss.
 */

import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'

import { LIMITS } from '@shared/constants'
import { BUDGET_LEVELS } from '@shared/types'
import { clampNumber } from '@shared/validation'

import { WizardFooter } from '../../../components/layout/wizard'
import { Card, CardContent } from '../../../components/ui/card'
import { Chip, ChipGroup } from '../../../components/ui/chip'
import { Field, FieldGroup } from '../../../components/ui/field'
import { Input, Textarea } from '../../../components/ui/input'
import { joinList } from '../../../lib/format'
import { useSharedOutlet } from '../shared-layout'
import { joinStepNeighbours, joinStepPath, useJoinDraft } from './layout'

/** Shortest commute the slider may express, matching the API's lower bound. */
const MIN_MAX_TRAVEL_MINUTES = 5
/** Slider granularity: quarter hours are as precise as anyone needs. */
const MAX_TRAVEL_STEP_MINUTES = 5

export function JoinPreferencesPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { shareToken } = useSharedOutlet()
  const { draft, update } = useJoinDraft(shareToken)
  const { previous, next } = joinStepNeighbours('preferences')

  /** Tags the form offers; free text is accepted by the API, not asked for here. */
  const avoidOptions = ['no_spicy', 'no_seafood', 'no_beef', 'vegetarian', 'no_alcohol']

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardContent className="flex flex-col gap-4 pt-4 sm:pt-5">
          <FieldGroup>
            <Field
              label={t('join.maxTravelLabel')}
              htmlFor="maxTravelMinutes"
              hint={t('join.maxTravelUnit', { minutes: draft.maxTravelMinutes })}
            >
              <Input
                id="maxTravelMinutes"
                name="maxTravelMinutes"
                type="range"
                min={MIN_MAX_TRAVEL_MINUTES}
                max={LIMITS.maxTravelMinutes}
                step={MAX_TRAVEL_STEP_MINUTES}
                value={draft.maxTravelMinutes}
                onChange={(event) =>
                  update({
                    maxTravelMinutes: clampNumber(
                      Number(event.target.value),
                      MIN_MAX_TRAVEL_MINUTES,
                      LIMITS.maxTravelMinutes,
                    ),
                  })
                }
              />
            </Field>
          </FieldGroup>

          <div className="flex flex-col gap-2">
            <h2 className="text-sm font-medium">{t('budget.label')}</h2>
            <ChipGroup label={t('budget.label')}>
              {BUDGET_LEVELS.map((level) => (
                <Chip
                  key={level}
                  selected={draft.budget === level}
                  onClick={() => update({ budget: level })}
                >
                  {t(`budget.${level}`)}
                </Chip>
              ))}
            </ChipGroup>
          </div>

          <FieldGroup>
            <Field
              label={t('join.noteLabel')}
              htmlFor="note"
              hint={t('join.notePlaceholder')}
            >
              <Textarea
                id="note"
                name="note"
                value={draft.note}
                maxLength={LIMITS.noteMaxLength}
                onChange={(event) => update({ note: event.target.value })}
              />
            </Field>
          </FieldGroup>

          {/* Everything below is optional: the step is scored on what it says, and a
              member who skips it declares nothing rather than declaring "no". */}
          <FieldGroup>
            <Field label={t('preference.avoidLabel')} hint={t('preference.optionalHint')}>
              <ChipGroup label={t('preference.avoidLabel')}>
                {avoidOptions.map((tag) => (
                  <Chip
                    key={tag}
                    selected={draft.dietaryAvoid.includes(tag)}
                    onClick={() =>
                      update({
                        dietaryAvoid: draft.dietaryAvoid.includes(tag)
                          ? draft.dietaryAvoid.filter((item) => item !== tag)
                          : [...draft.dietaryAvoid, tag],
                      })
                    }
                  >
                    {t(`preference.avoid.${tag}`)}
                  </Chip>
                ))}
              </ChipGroup>
            </Field>
            <Field
              label={t('preference.wantsLabel')}
              htmlFor="wants"
              hint={t('preference.wantsHint')}
            >
              <Input
                id="wants"
                name="wants"
                value={joinList(draft.wants, t)}
                placeholder={t('preference.wantsPlaceholder')}
                onChange={(event) =>
                  update({
                    wants: event.target.value
                      .split(/[、,，]/)
                      .map((item) => item.trim())
                      .filter((item) => item !== '')
                      .slice(0, 5),
                  })
                }
              />
            </Field>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={draft.selfArrangeAllowed}
                onChange={(event) => update({ selfArrangeAllowed: event.target.checked })}
              />
              {t('preference.selfArrange')}
            </label>
            {/* The switch changes what the engine can recommend, so it states the
                consequence instead of leaving the member to guess. */}
            <p className="text-muted-foreground text-xs">{t('preference.selfArrangeHint')}</p>
            <p className="text-muted-foreground text-xs">{t('preference.visibility')}</p>
          </FieldGroup>
        </CardContent>
      </Card>

      <WizardFooter
        backTo={previous ? joinStepPath(shareToken, previous) : undefined}
        backLabel={t('common.back')}
        onNext={() => {
          if (next) {
            navigate(joinStepPath(shareToken, next))
          }
        }}
        nextLabel={t('common.next')}
      />
    </div>
  )
}
